<template>
  <section class="section is-medium platforms-section">
    <div class="container">
      <div class="has-text-centered mb-6">
        <h2 class="title is-2 has-line animate fade-in-up">
          {{ $t('landing.platforms-section.title') }}
        </h2>
        <p class="subtitle is-5 animate fade-in-up animate--delay-1">
          {{ $t('landing.platforms-section.subtitle') }}
        </p>
      </div>

      <!-- Major Platforms -->
      <div class="platforms-category mb-6">
        <h3 class="title is-4 has-text-centered mb-5">
          {{ $t('landing.platforms-section.major-platforms') }}
        </h3>
        <div class="platforms-grid major-platforms">
          <div
            v-for="platform in majorPlatforms"
            :key="platform.name"
            class="platform-item"
          >
            <div class="platform-logo">
              <img
                v-if="platform.logo"
                :src="platform.logo"
                :alt="platform.name"
              >
              <span
                v-else
                class="platform-fallback"
              >
                {{ platform.name.length > 0 ? platform.name.charAt(0) : '?' }}
              </span>
            </div>
            <span class="platform-name">{{ platform.name }}</span>
          </div>
        </div>
      </div>

      <!-- Premium Platforms (hidden for now) -->
    </div>
  </section>
</template>

<script setup>
import { ref } from 'vue';

// Import logo assets
import spotifyLogo from '@/assets/images/landing/spotify-logo.svg';
import appleMusicLogo from '@/assets/images/landing/apple-music.svg';
import amazonMusicLogo from '@/assets/images/landing/amazon-music-logo.svg';
import youtubeMusicLogo from '@/assets/images/landing/youtube-music-logo.svg';
import tiktokLogo from '@/assets/images/landing/tiktok-logo.svg';
import deezerLogo from '@/assets/images/landing/deezer-logo.svg';
import pandoraLogo from '@/assets/images/landing/pandora-logo.svg';
import tidalLogo from '@/assets/images/landing/tidal-logo.svg';
import anghamiLogo from '@/assets/images/landing/anghami-logo.png';
import boomplayLogo from '@/assets/images/landing/boomplay-logo.png';
import neteaseLogo from '@/assets/images/landing/netease-logo.svg';
import qqMusicLogo from '@/assets/images/landing/qq-music-logo.svg';
import soundcloudLogo from '@/assets/images/landing/soundcloud-logo.svg';
import lineLogo from '@/assets/images/landing/line-logo.svg';
import trebelLogo from '@/assets/images/landing/trebel-logo.png';
import iHeartRadioLogo from '@/assets/images/landing/iheart-radio-logo.png';
import imusicaLogo from '@/assets/images/landing/imusica-logo.svg';

const majorPlatforms = ref([
  { name: 'Spotify', logo: spotifyLogo },
  { name: 'Apple Music', logo: appleMusicLogo },
  { name: 'Amazon Music', logo: amazonMusicLogo },
  { name: 'YouTube Music', logo: youtubeMusicLogo },
  { name: 'TikTok', logo: tiktokLogo },
  { name: 'Deezer', logo: deezerLogo },
  { name: 'Pandora', logo: pandoraLogo },
  { name: 'TIDAL', logo: tidalLogo },
  { name: 'iHeartRadio', logo: iHeartRadioLogo },
  { name: 'Anghami', logo: anghamiLogo },
  { name: 'Boomplay', logo: boomplayLogo },
  { name: 'NetEase', logo: neteaseLogo },
  { name: 'QQ Music', logo: qqMusicLogo },
  { name: 'Yandex Music' },
  { name: 'Qobuz' },
  { name: 'Audiomack' },
  { name: 'SoundCloud Content ID', logo: soundcloudLogo },
  { name: 'JioSaavn' },
  { name: 'United Media Agency (UMA)' },
  { name: 'Nuuday A/S' },
  { name: 'AWA' },
  { name: 'Facebook Audio Library' },
  { name: 'Facebook Fingerprinting' },
  { name: 'Gracenote' },
  { name: 'iMusica', logo: imusicaLogo },
  { name: 'LINE Music', logo: lineLogo },
  { name: 'Taobao' },
  { name: 'Fizy' },
  { name: 'FLO Music' },
  { name: 'Bugs' },
  { name: 'Genie Music' },
  { name: 'Naver' },
  { name: 'Trebel', logo: trebelLogo },
]);

// TODO: For now we will mix all of them in the major platforms section
</script>

<style scoped lang="scss">
@import "@/styles/abstracts/colors";
@import "@/styles/abstracts/animations";

.platforms-section {
  background: linear-gradient(135deg,
    rgba(lighten($indiefy-blue, 45%), 0.3) 0%,
    rgba(lighten($indiefy-lavender, 35%), 0.3) 100%
  );

  .body--dark & {
    background: linear-gradient(135deg,
      rgba(darken($indiefy-blue, 10%), 0.2) 0%,
      rgba(darken($indiefy-lavender, 0%), 0.2) 100%
    );
  }
}

.title.has-line {
  position: relative;
  padding-bottom: 1.5rem;
  color: var(--color-text, #000);

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: linear-gradient(90deg, $indiefy-blue, $indiefy-pink);
    border-radius: 2px;
  }
}

.platforms-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 2rem;
  max-width: 800px;
  margin: 0 auto;

  &.premium-platforms {
    grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
    gap: 1.5rem;
  }
}

.platform-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 1rem;
  background: var(--color-background, white);
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  }

  .platform-logo {
    width: 60px;
    height: 60px;
    margin-bottom: 0.75rem;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
    }

    .platform-fallback {
      font-size: 1.5rem;
      font-weight: 700;
      color: $indiefy-blue;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;

      .body--dark & {
        color: white;
      }
    }
  }

  .platform-name {
    font-size: 0.9rem;
    font-weight: 600;
    color: var(--color-text, #000);
  }
}

@media (max-width: 768px) {
  .platforms-grid {
    grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
    gap: 1rem;
  }

  .platform-item {
    padding: 0.75rem;

    .platform-logo {
      width: 50px;
      height: 50px;
    }

    .platform-name {
      font-size: 0.8rem;
    }
  }
}
</style>